import { api } from "@/openapi/client";
import { operations } from "@/openapi/generated";
import { ApiResponseError } from "@/lib/error";
import { getCacheTags } from "@/lib/cache-utils";
import { merchantQueryKeys } from "@/app/admin/merchants/_api/merchant-query-keys";

/**
 * @module merchant-actions
 *
 * Naming Convention:
 * - Request Parameters:        <OperationName>RequestParameter — the path or query parameters for the HTTP request.
 * - Request Bodies:            <OperationName>RequestBody    — the JSON payload to send in POST/PATCH/PUT.
 * - Response Types:            <OperationName>Response       — the type returned by the HTTP call.
 *
 * Each helper groups its path-parameter type, request-body type, and response type using OpenAPI
 * operations[...] indexed types, and then bundles them into a single function that handles API calls,
 * error-checking, and URL construction.
 */

export type GetAllMerchantsParameter = operations["getAllMerchants"]["parameters"]["query"]
export type GetAllMerchantsResponse = operations["getAllMerchants"]["responses"]["200"]["content"]["*/*"]

export async function getAllMerchants(
  query: GetAllMerchantsParameter,
  client: typeof api = api
): Promise<GetAllMerchantsResponse> {
  const { data, error } = await client.GET("/merchants",
    {
      params: { query },
      next: { tags: getCacheTags(merchantQueryKeys.list({
        page: query?.page,
        size: query?.size,
        term: query?.term,
        sort: query?.sort,
        networkId: query?.networkId,
        regions: query?.regions,
        merchantStatuses: query?.merchantStatuses?.map(String),
        contractStatuses: query?.contractStatuses?.map(String),
        hasShop: query?.hasShop,
      })) },
      // Note: No 'cache: force-cache' since this is admin-only (authenticated)
    }
  );
  if (error) throw error;
  if (!data) throw new ApiResponseError('getAllMerchants');
  return data;
}

export type MerchantByIdParameter = operations["getMerchantById"]["parameters"]["path"]["id"]
export type MerchantByIdResponse = operations["getMerchantById"]["responses"]["200"]["content"]["*/*"]

export async function getMerchantById(
  id: MerchantByIdParameter,
  client: typeof api = api
): Promise<MerchantByIdResponse> {
  const { data, error } = await client.GET("/merchants/{id}/withAssociated", {
    params: { path : { id }  },
    next: { tags: getCacheTags(merchantQueryKeys.detail(id)) },
    // Note: No 'cache: force-cache' since this is admin-only (authenticated)
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError('getMerchantById');
  return data;
}

export type GetMerchantsByDomainResponse = operations["getMerchantsByDomain"]["responses"]["200"]["content"]["*/*"]

export async function getMerchantsByDomain(
  url: string,
  client: typeof api = api
): Promise<GetMerchantsByDomainResponse> {
  const { data, error } = await client.GET("/merchants/byDomain", {
    params: { query: { url } },
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getMerchantsByDomain");
  return data;
}

export async function setMerchantShop(
  merchantId: string,
  shopId: string | null,
  client: typeof api = api
): Promise<MerchantByIdResponse> {
  const { data, error } = await client.PUT("/merchants/{id}/shop", {
    params: { path: { id: merchantId } },
    body: { shopId: shopId ?? undefined },
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("setMerchantShop");
  return data as MerchantByIdResponse;
}



